import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { 
  Gift, 
  Trophy,
  Loader2,
  Crown,
  Sparkles,
  Users,
  ChevronLeft,
  ChevronRight,
  Heart,
  Maximize,
  Settings,
  X,
  Timer,
  Building2,
  Monitor,
  Camera
} from "lucide-react";
import { publicAPI } from "../lib/api";
import { getImageUrl } from "../lib/utils";
import PhotoWallMosaic from "../components/PhotoWallMosaic";

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return "";
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : "";
};

// Available aspect ratios
const ASPECT_RATIOS = {
  "16:9": { width: 16, height: 9, label: "16:9 (HD)" },
  "3:2": { width: 3, height: 2, label: "3:2 (Écran 3x2m)" },
  "16:10": { width: 16, height: 10, label: "16:10 (MacBook)" },
  "4:3": { width: 4, height: 3, label: "4:3 (Standard)" },
  "21:9": { width: 21, height: 9, label: "21:9 (Ultra-wide)" },
};

// Animated words for TIREO logo
const WORDS = ["hasard", "sort", "clic", "pif"];

// Animated promo messages with static URL
const TypewriterPromo = () => {
  const messages = [
    { text: "Vous êtes une", highlight: "Association", suffix: "? Animez vos événements !" },
    { text: "Vous êtes un", highlight: "Club de Sport", suffix: "? Engagez vos supporters !" },
    { text: "Vous êtes une", highlight: "Entreprise", suffix: "? Boostez votre marketing !" }
  ];
  
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const currentMessage = messages[messageIndex];

  return (
    <div className="text-center max-w-xl mx-auto">
      <div className="min-h-[1.5rem] mb-0.5">
        <AnimatePresence mode="wait">
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-white/80 text-base font-light leading-relaxed"
          >
            {currentMessage.text}{" "}
            <span className="text-yellow-400 font-black text-xl">
              {currentMessage.highlight}
            </span>
            {" "}{currentMessage.suffix}
          </motion.p>
        </AnimatePresence>
      </div>
      <p className="text-white/50 text-xs mb-0.5">Créez votre compte sur</p>
      <a 
        href="https://www.tireo.app" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-blue-400 text-xl font-bold hover:text-blue-300 transition-colors"
      >
        tireo.app
      </a>
    </div>
  );
};

// Animated TIREO component
const AnimatedTireoFooter = () => {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex(prev => (prev + 1) % WORDS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center justify-center gap-2">
      <span className="text-xl text-white/80 font-medium">Propulsé par</span>
      <span className="font-black text-3xl text-blue-500">TIREO</span>
      <span className="relative w-16 h-8 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.span
            key={wordIndex}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute left-0 text-white text-xl font-medium"
          >
            {WORDS[wordIndex]}
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  );
};

export default function PublicDrawShow() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPrizeSlide, setCurrentPrizeSlide] = useState(0);
  const [currentPartnerSlide, setCurrentPartnerSlide] = useState(0);
  const [prizeResults, setPrizeResults] = useState([]);
  const [countdown, setCountdown] = useState(null);
  const [suspenseMessage, setSuspenseMessage] = useState("");
  const prizeInterval = useRef(null);
  const partnerInterval = useRef(null);
  const containerRef = useRef(null);
  
  // Projector settings
  const [showSettings, setShowSettings] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(searchParams.get("ratio") || "16:9");
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Photo wall state
  const photoWallEnabled = draw?.photo_wall_config?.enabled;
  const [showPhotoWall, setShowPhotoWall] = useState(false);

  // Show results when completed AND results are broadcast, or when explicitly completed
  const isCompleted = draw?.status === "completed";
  const showResults = isCompleted && draw?.results_broadcast;

  // Suspense messages
  const suspenseMessages = {
    far: ["Les lots vous attendent !", "Préparez-vous !"],
    soon: ["Plus que quelques heures !", "Le suspense monte..."],
    imminent: ["C'est bientôt !", "Croisez les doigts !"],
    veryClose: ["C'EST PARTI !", "LE TIRAGE COMMENCE !"]
  };

  const getSuspenseCategory = () => {
    if (!countdown) return "far";
    const totalSeconds = countdown.total / 1000;
    if (totalSeconds <= 60) return "veryClose";
    if (totalSeconds <= 300) return "imminent";
    if (totalSeconds <= 3600) return "soon";
    return "far";
  };

  // Handle ratio change
  const handleRatioChange = (newRatio) => {
    setAspectRatio(newRatio);
    setSearchParams({ ratio: newRatio });
  };

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    loadDraw();
    const interval = setInterval(loadDraw, 10000);
    
    const msgInterval = setInterval(() => {
      const category = getSuspenseCategory();
      const messages = suspenseMessages[category];
      setSuspenseMessage(messages[Math.floor(Math.random() * messages.length)]);
    }, 3000);
    
    return () => {
      clearInterval(interval);
      clearInterval(msgInterval);
      if (prizeInterval.current) clearInterval(prizeInterval.current);
      if (partnerInterval.current) clearInterval(partnerInterval.current);
    };
  }, [id]);

  // Countdown timer
  useEffect(() => {
    if (draw?.draw_date && !isCompleted) {
      const updateCountdown = () => {
        const now = new Date();
        const drawDate = new Date(draw.draw_date);
        const diff = drawDate - now;
        
        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);
          
          setCountdown({ days, hours, minutes, seconds, total: diff });
        } else {
          setCountdown(null);
        }
      };
      
      updateCountdown();
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [draw?.draw_date, isCompleted]);

  // Auto-slide for prizes
  useEffect(() => {
    const items = showResults ? prizeResults : draw?.prizes || [];
    if (items.length > 1) {
      prizeInterval.current = setInterval(() => {
        setCurrentPrizeSlide(prev => (prev + 1) % items.length);
      }, showResults ? 6000 : 5000);
    }
    return () => {
      if (prizeInterval.current) clearInterval(prizeInterval.current);
    };
  }, [prizeResults.length, draw?.prizes?.length, showResults]);

  // Auto-slide for partners
  useEffect(() => {
    const partners = draw?.partner_logos || [];
    if (partners.length > 1) {
      partnerInterval.current = setInterval(() => {
        setCurrentPartnerSlide(prev => (prev + 1) % partners.length);
      }, 4000);
    }
    return () => {
      if (partnerInterval.current) clearInterval(partnerInterval.current);
    };
  }, [draw?.partner_logos?.length]);

  // Confetti on winners when results are shown
  useEffect(() => {
    if (showResults && prizeResults[currentPrizeSlide]?.winners?.length > 0) {
      fireConfetti();
    }
  }, [currentPrizeSlide, prizeResults, showResults]);

  const fireConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#FF6347', '#FF1493', '#00CED1']
    });
  };

  const loadDraw = async () => {
    try {
      const res = await publicAPI.getDraw(id);
      setDraw(res.data);
      
      if (res.data.status === "completed" && res.data.results?.length > 0) {
        const prizeMap = {};
        res.data.results.forEach(r => {
          const pId = r.prize_id;
          if (!prizeMap[pId]) {
            // Always get the full prize from prizes array to have complete data (image, etc.)
            const fullPrize = res.data.prizes?.find(p => p.id === pId);
            prizeMap[pId] = {
              prize: fullPrize || r.prize,
              prize_name: r.prize_name || fullPrize?.name,
              winners: []
            };
          }
          prizeMap[pId].winners.push(r.participant);
        });
        setPrizeResults(Object.values(prizeMap));
      }
    } catch (error) {
      console.error("Error loading draw:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get background style
  const getBackgroundStyle = () => {
    if (draw?.background_type === "image" && draw?.background_image) {
      return {
        backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${getImageUrl(draw.background_image)})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      };
    }
    if (draw?.background_type === "color" && draw?.background_color) {
      return {
        background: `linear-gradient(135deg, ${draw.background_color} 0%, #0f172a 100%)`
      };
    }
    return { background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)' };
  };

  const hasVideoBackground = draw?.background_type === "video" && draw?.background_video_url;
  const videoId = hasVideoBackground ? getYouTubeId(draw.background_video_url) : null;

  // Calculate container dimensions based on aspect ratio
  const getContainerStyle = () => {
    const ratio = ASPECT_RATIOS[aspectRatio];
    if (!ratio) return { width: '100vw', height: '100vh' };
    
    if (aspectRatio === "16:9") {
      return { width: '100vw', height: '100vh' };
    }
    
    return {
      aspectRatio: `${ratio.width} / ${ratio.height}`,
      maxWidth: '100vw',
      maxHeight: '100vh',
      margin: 'auto'
    };
  };

  if (loading) {
    return (
      <div className="h-screen w-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2 }}>
          <Loader2 className="w-16 h-16 text-yellow-400" />
        </motion.div>
      </div>
    );
  }

  if (!draw) return null;

  const prizes = showResults ? prizeResults : draw.prizes || [];
  const currentPrize = prizes[currentPrizeSlide];
  const partners = draw.partner_logos || [];
  const currentPartner = partners[currentPartnerSlide];

  return (
    <div 
      ref={containerRef}
      className="h-screen w-screen overflow-hidden relative flex items-center justify-center"
      style={{ background: '#000' }}
    >
      {/* Ratio Container */}
      <div 
        className="relative overflow-hidden"
        style={{
          ...getContainerStyle(),
          ...(hasVideoBackground ? {} : getBackgroundStyle())
        }}
      >
        {/* YouTube Video Background */}
        {hasVideoBackground && videoId && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 bg-black/70 z-10" />
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              className="absolute top-1/2 left-1/2 w-[200vw] h-[200vh] -translate-x-1/2 -translate-y-1/2"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        )}

        {/* Settings Button */}
        <div className="absolute top-4 right-4 z-50 flex gap-2">
          <button
            onClick={toggleFullscreen}
            className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-xl backdrop-blur transition-all"
            title="Plein écran"
          >
            <Maximize className="w-5 h-5" />
          </button>
          {photoWallEnabled && (
            <button
              onClick={() => setShowPhotoWall(!showPhotoWall)}
              className={`p-3 rounded-xl backdrop-blur transition-all ${
                showPhotoWall 
                  ? 'bg-green-500 text-white' 
                  : 'bg-black/50 hover:bg-black/70 text-white'
              }`}
              title="Mur photos"
            >
              <Camera className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="bg-black/50 hover:bg-black/70 text-white p-3 rounded-xl backdrop-blur transition-all"
            title="Paramètres"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              className="absolute top-16 right-4 z-50 bg-black/90 backdrop-blur-xl rounded-2xl p-6 w-72 border border-white/20"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold">Mode Projection</h3>
                <button onClick={() => setShowSettings(false)} className="text-white/60 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-white/70 text-sm block mb-2">Ratio d'écran</label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(ASPECT_RATIOS).map(([key, { label }]) => (
                      <button
                        key={key}
                        onClick={() => handleRatioChange(key)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          aspectRatio === key
                            ? "bg-yellow-500 text-black"
                            : "bg-white/10 text-white hover:bg-white/20"
                        }`}
                      >
                        {key}
                      </button>
                    ))}
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    {ASPECT_RATIOS[aspectRatio]?.label || aspectRatio}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ============ MAIN 3-COLUMN LAYOUT ============ */}
        <div className="h-full w-full flex relative z-20 p-4 gap-4">
          
          {/* ============ LEFT COLUMN - Countdown & Partners ============ */}
          <div className="w-1/4 flex flex-col gap-3">
            {/* BIG COUNTDOWN */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-gradient-to-br from-yellow-500/20 to-amber-500/10 backdrop-blur-xl rounded-2xl p-5 border border-yellow-500/30"
            >
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Timer className="w-8 h-8 text-yellow-400" />
                  <span className="text-yellow-400 font-bold text-xl uppercase tracking-wider">
                    {showResults ? "RÉSULTATS" : (isCompleted ? "TERMINÉ" : "TIRAGE DANS")}
                  </span>
                </div>
                
                {countdown && !showResults && !isCompleted ? (
                  <div className="space-y-2">
                    {countdown.days > 0 && (
                      <div className="bg-black/30 rounded-xl p-2">
                        <p className="text-4xl font-black text-white">{countdown.days}</p>
                        <p className="text-yellow-400/80 text-xs">JOURS</p>
                      </div>
                    )}
                    
                    <div className="flex gap-1 justify-center">
                      <div className="bg-black/30 rounded-xl p-2 flex-1">
                        <motion.p 
                          className="text-3xl font-black text-white"
                          key={countdown.hours}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                        >
                          {String(countdown.hours).padStart(2, '0')}
                        </motion.p>
                        <p className="text-white/60 text-[10px]">HEURES</p>
                      </div>
                      <div className="bg-black/30 rounded-xl p-2 flex-1">
                        <motion.p 
                          className="text-3xl font-black text-white"
                          key={countdown.minutes}
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                        >
                          {String(countdown.minutes).padStart(2, '0')}
                        </motion.p>
                        <p className="text-white/60 text-[10px]">MIN</p>
                      </div>
                      <div className="bg-black/30 rounded-xl p-2 flex-1">
                        <motion.p 
                          className="text-3xl font-black text-yellow-400"
                          key={countdown.seconds}
                          initial={{ scale: 1.3 }}
                          animate={{ scale: 1 }}
                        >
                          {String(countdown.seconds).padStart(2, '0')}
                        </motion.p>
                        <p className="text-yellow-400/80 text-[10px]">SEC</p>
                      </div>
                    </div>

                    <motion.p 
                      key={suspenseMessage}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-white/70 text-xs font-medium mt-1"
                    >
                      {suspenseMessage}
                    </motion.p>
                  </div>
                ) : (
                  <div className="py-3">
                    <Trophy className="w-14 h-14 text-yellow-400 mx-auto mb-2" />
                    <p className="text-white font-bold text-sm">Félicitations !</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Participants Count */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
              <div className="flex items-center justify-center gap-3">
                <Users className="w-8 h-8 text-yellow-400" />
                <div className="text-center">
                  <p className="text-3xl font-black text-white">
                    {(draw.participants?.length || 0) + (draw.manual_participant_count || 0)}
                  </p>
                  <p className="text-white/60 text-sm">Participants</p>
                </div>
              </div>
            </div>

            {/* Partners Carousel */}
            {draw.partner_carousel_enabled && partners.length > 0 && (
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 flex-1 flex flex-col">
                <div className="text-center mb-4">
                  <p className="text-white/70 text-lg font-medium uppercase tracking-wider">
                    Merci à
                  </p>
                  <p className="text-white text-3xl font-black uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
                    Nos Partenaires
                  </p>
                </div>
                
                <div className="flex-1 flex items-center justify-center min-h-[220px]">
                  <div className="bg-white rounded-3xl p-10 shadow-2xl">
                    {currentPartner?.url && (
                      <img
                        key={currentPartnerSlide}
                        src={getImageUrl(currentPartner.url)}
                        alt=""
                        className="h-44 w-auto max-w-[300px] object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>

                {partners.length > 1 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {partners.map((_, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => setCurrentPartnerSlide(idx)}
                        className={`h-3 rounded-full transition-all ${idx === currentPartnerSlide ? "bg-white w-8" : "bg-white/30 w-3"}`} 
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ============ CENTER COLUMN - Title + Prizes/Photo Wall + TIREO Footer ============ */}
          <div className="w-1/2 flex flex-col">
            {/* TITLE */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="text-center mt-16 mb-4"
            >
              <h1 
                className="text-5xl lg:text-7xl font-black text-white"
                style={{ fontFamily: 'Outfit', textShadow: '0 0 40px rgba(234, 179, 8, 0.4)' }}
              >
                {draw.title}
              </h1>
            </motion.div>

            {/* Show Photo Wall Mosaic or Regular Content */}
            {showPhotoWall ? (
              <div className="flex-1 flex items-center justify-center p-4">
                <PhotoWallMosaic drawId={id} className="w-full max-w-2xl" />
              </div>
            ) : showResults && prizeResults.length > 0 ? (
              /* Winners Display */
              <>
                <div className="text-center mb-3">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-amber-500 px-5 py-2 rounded-full">
                    <Trophy className="w-5 h-5 text-white" />
                    <span className="text-lg font-bold text-white">GAGNANTS</span>
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    {currentPrize && (
                      <motion.div
                        key={currentPrizeSlide}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="w-full bg-gradient-to-br from-yellow-500/20 via-white/10 to-amber-500/20 backdrop-blur-xl rounded-3xl overflow-hidden border border-yellow-500/50"
                      >
                        <div className="flex flex-col lg:flex-row">
                          <div className="lg:w-2/5 h-56 lg:h-72 relative">
                            {currentPrize.prize?.image_url ? (
                              <img src={getImageUrl(currentPrize.prize.image_url)} alt={currentPrize.prize_name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-yellow-500/40 to-amber-600/40 flex items-center justify-center">
                                <Gift className="w-20 h-20 text-yellow-400/60" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 p-5">
                            <div className="flex items-center gap-2 mb-4">
                              <Crown className="w-6 h-6 text-yellow-400" />
                              <span className="text-yellow-400 font-bold text-xl uppercase">
                                {currentPrize.prize_name || currentPrize.prize?.name}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {currentPrize.winners?.slice(0, 3).map((winner, idx) => (
                                <motion.div
                                  key={idx}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: idx * 0.1 }}
                                  className="flex items-center gap-3 bg-white/10 rounded-xl p-3"
                                >
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                                    <span className="text-lg font-black text-white">{idx + 1}</span>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-lg font-bold text-white">{winner.display_name || winner.pseudo || winner.name}</p>
                                    {draw?.custom_field_label && winner.custom_field_value && (
                                      <p className="text-sm text-yellow-400/80">
                                        {draw.custom_field_label}: {winner.custom_field_value}
                                      </p>
                                    )}
                                  </div>
                                  <Sparkles className="w-5 h-5 text-yellow-400" />
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {prizeResults.length > 1 && (
                  <div className="flex justify-center gap-2 mt-3">
                    {prizeResults.map((_, idx) => (
                      <button key={idx} onClick={() => setCurrentPrizeSlide(idx)} className={`h-2 rounded-full transition-all ${idx === currentPrizeSlide ? "bg-yellow-400 w-8" : "bg-white/30 w-2"}`} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Prizes Display */
              <>
                <div className="text-center mb-2">
                  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 px-4 py-1.5 rounded-full">
                    <Gift className="w-4 h-4 text-white" />
                    <span className="text-sm font-bold text-white">LOTS À GAGNER</span>
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center relative">
                  {/* Navigation arrows */}
                  {prizes.length > 1 && (
                    <>
                      <button onClick={() => setCurrentPrizeSlide(prev => (prev - 1 + prizes.length) % prizes.length)} className="absolute left-0 z-30 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full p-3">
                        <ChevronLeft className="w-6 h-6 text-white" />
                      </button>
                      <button onClick={() => setCurrentPrizeSlide(prev => (prev + 1) % prizes.length)} className="absolute right-0 z-30 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full p-3">
                        <ChevronRight className="w-6 h-6 text-white" />
                      </button>
                    </>
                  )}

                  <AnimatePresence mode="wait">
                    {currentPrize && (
                      <motion.div
                        key={currentPrizeSlide}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        className="w-full max-w-3xl mx-6"
                      >
                        <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/20">
                          <div className="flex flex-row h-[420px]">
                            {/* Prize Image */}
                            <div className="w-1/2 h-full relative">
                              {currentPrize.image_url ? (
                                <img src={getImageUrl(currentPrize.image_url)} alt={currentPrize.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-yellow-500/30 to-amber-600/30 flex items-center justify-center">
                                  <Gift className="w-32 h-32 text-yellow-400/50" />
                                </div>
                              )}
                              {/* Quantity badge - top right */}
                              <div className="absolute top-4 right-4 bg-yellow-500 text-black font-black px-5 py-2 rounded-full text-2xl">
                                x{currentPrize.quantity || 1}
                              </div>
                              {/* Value badge - top left */}
                              {currentPrize.value && (
                                <div className="absolute top-4 left-4 bg-green-500 text-white font-bold px-4 py-2 rounded-full text-lg">
                                  {currentPrize.value}
                                </div>
                              )}
                              {/* Partner logo - bottom right */}
                              {currentPrize.partner_logo_url && (
                                <div className="absolute bottom-4 right-4 bg-white rounded-xl p-3">
                                  <img src={getImageUrl(currentPrize.partner_logo_url)} alt="" className="h-12 w-auto object-contain" />
                                </div>
                              )}
                            </div>

                            {/* Prize Info */}
                            <div className="w-1/2 p-8 flex flex-col justify-center">
                              <h3 className="text-3xl lg:text-4xl font-black text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                                {currentPrize.name}
                              </h3>
                              {currentPrize.description && (
                                <p className="text-white/70 text-lg mb-6 line-clamp-4">{currentPrize.description}</p>
                              )}
                              {currentPrize.partner_name && (
                                <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl mt-auto">
                                  <span className="text-white/50 text-base">Offert par</span>
                                  <span className="text-white font-bold text-xl">{currentPrize.partner_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Prize dots */}
                {prizes.length > 1 && (
                  <div className="flex justify-center gap-2 mt-3 mb-6">
                    {prizes.map((_, idx) => (
                      <button key={idx} onClick={() => setCurrentPrizeSlide(idx)} className={`h-2 rounded-full transition-all ${idx === currentPrizeSlide ? "bg-yellow-400 w-8" : "bg-white/30 w-2"}`} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* TIREO Footer */}
            <div className="flex flex-col items-center justify-center mt-auto mb-1">
              <div className="w-32 h-px bg-white/30 mb-2" />
              <AnimatedTireoFooter />
              <div className="mt-1">
                <TypewriterPromo />
              </div>
            </div>
          </div>

          {/* ============ RIGHT COLUMN - Organizer & Beneficiary ============ */}
          <div className="w-1/4 flex flex-col gap-3">
            {/* Organizer */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20 flex-[2] flex flex-col items-center justify-center text-center"
            >
              {draw.organizer_logo ? (
                <div className="bg-white rounded-2xl p-4 shadow-xl mb-3">
                  <img 
                    src={getImageUrl(draw.organizer_logo)} 
                    alt={draw.organizer_name}
                    className="h-20 w-auto object-contain"
                  />
                </div>
              ) : (
                <div className="h-24 w-24 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-xl mb-3">
                  <Building2 className="w-12 h-12 text-white" />
                </div>
              )}
              
              <p className="text-white/70 text-lg font-semibold uppercase tracking-wider mb-2">Organisé par</p>
              <h2 className="text-white text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {draw.organizer_name}
              </h2>
            </motion.div>

            {/* Beneficiary */}
            {draw.beneficiary_name && (
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-pink-500/20 to-rose-500/10 backdrop-blur-xl rounded-2xl p-5 border border-pink-500/30 flex-[3] flex flex-col items-center justify-center text-center"
              >
                <p className="text-pink-300 text-lg font-bold uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
                  <Heart className="w-6 h-6" />
                  AU PROFIT DE
                </p>
                
                {draw.beneficiary_logo ? (
                  <div className="bg-white rounded-2xl p-5 mx-auto mb-4 shadow-xl">
                    <img 
                      src={getImageUrl(draw.beneficiary_logo)} 
                      alt={draw.beneficiary_name}
                      className="h-24 w-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-24 w-24 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                    <Heart className="w-12 h-12 text-white" />
                  </div>
                )}
                
                <p className="text-pink-300 text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                  {draw.beneficiary_name}
                </p>
              </motion.div>
            )}

            {!draw.beneficiary_name && (
              <div className="flex-[3]" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
